import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { useTranslation } from 'react-i18next';
import Footer from '../components/Footer';
import LocationPicker from '../components/LocationPicker';
import PHAddressSelect from '../components/PHAddressSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateJob = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [savedLocations, setSavedLocations] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    budget: '',
    currency: 'PHP',
    job_type: 'time_bound',
    location: '',
    location_coordinates: null,
    preferred_date: '',
    preferred_start_time: '',
    preferred_end_time: '',
    deadline: '',
    bidding_deadline_date: '',  // When bidding closes - date part
    bidding_deadline_time: '',  // When bidding closes - time part
    images: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useCustomLocation, setUseCustomLocation] = useState(false);
  const [customAddr, setCustomAddr] = useState({ province: '', city: '', barangay: '' });
  const [useRegisteredAddress, setUseRegisteredAddress] = useState(true);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdJobCode, setCreatedJobCode] = useState('');

  // Build user's registered address string
  const getUserRegisteredAddress = () => {
    if (!user) return '';
    const parts = [
      user.street_address,
      user.barangay,
      user.city,
      user.province
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : user.location || '';
  };

  useEffect(() => {
    fetchCategories();
    fetchSavedLocations();
    // Set default location to user's registered address
    const registeredAddress = getUserRegisteredAddress();
    if (registeredAddress) {
      setFormData(prev => ({
        ...prev,
        location: registeredAddress,
        location_coordinates: user?.location_coordinates || null
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSavedLocations = async () => {
    try {
      const response = await axios.get(`${API}/user/locations`);
      setSavedLocations(response.data);
      
      // Only set saved location as default if user has no registered address
      const registeredAddress = getUserRegisteredAddress();
      if (!registeredAddress && response.data.length > 0) {
        const defaultLoc = response.data.find(loc => loc.is_default) || response.data[0];
        if (defaultLoc) {
          // Build address from individual fields if full_address is empty
          const address = defaultLoc.full_address || [defaultLoc.street, defaultLoc.barangay, defaultLoc.city, defaultLoc.province].filter(Boolean).join(', ');
          setFormData(prev => ({
            ...prev,
            location: address,
            location_coordinates: defaultLoc.coordinates
          }));
          setUseRegisteredAddress(false);
        }
      }
    } catch (error) {
      console.error('Error fetching saved locations:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // Preferred date can be the bidding-close date (same day) or later
      if (next.bidding_deadline_date && next.preferred_date &&
          next.preferred_date < next.bidding_deadline_date) {
        next.preferred_date = '';
      }
      // Completion deadline must be on/after the preferred date
      if (next.preferred_date && next.deadline && next.deadline < next.preferred_date) {
        next.deadline = '';
      }
      // Same-day start time must be at least 2 hours after bidding closes
      const sameDay = next.preferred_date && next.bidding_deadline_date &&
        next.preferred_date === next.bidding_deadline_date;
      if (sameDay && next.preferred_start_time) {
        const sb = addHoursToTime(next.bidding_deadline_time || '23:59', 2);
        if (!sb || sb.overflow || next.preferred_start_time < sb.time) {
          next.preferred_start_time = '';
        }
      }
      // End time must be at least 2 hours after the start time (or cleared if no start)
      if (!next.preferred_start_time) {
        next.preferred_end_time = '';
      } else if (next.preferred_end_time) {
        const eb = addHoursToTime(next.preferred_start_time, 2);
        if (!eb || eb.overflow || next.preferred_end_time < eb.time) {
          next.preferred_end_time = '';
        }
      }
      return next;
    });
  };

  const handleSavedLocationSelect = (locationId) => {
    if (locationId === 'custom') {
      setUseCustomLocation(true);
      setUseRegisteredAddress(false);
      setFormData({ ...formData, location: '', location_coordinates: null });
    } else {
      setUseCustomLocation(false);
      setUseRegisteredAddress(false);
      const loc = savedLocations.find(l => l.id === locationId);
      if (loc) {
        // Build address from individual fields if full_address is empty
        const address = loc.full_address || [loc.street, loc.barangay, loc.city, loc.province].filter(Boolean).join(', ');
        setFormData({
          ...formData,
          location: address,
          location_coordinates: loc.coordinates
        });
      }
    }
  };

  const handleLocationSelect = (selectedLocation) => {
    if (selectedLocation) {
      setFormData({
        ...formData,
        location: selectedLocation.name,
        location_coordinates: selectedLocation.coordinates
      });
    } else {
      setFormData({
        ...formData,
        location: '',
        location_coordinates: null
      });
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (formData.images.length + files.length > 5) {
      setError('Maximum 5 images allowed for job posting');
      return;
    }

    setUploadingImages(true);
    setError('');

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Each file must be less than 5MB');
        continue;
      }

      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        setError('Only JPEG, PNG, and WebP images are allowed');
        continue;
      }

      try {
        const uploadData = new FormData();
        uploadData.append('file', file);

        const token = localStorage.getItem('token');
        const response = await axios.post(`${API}/upload/service-image`, uploadData, {
          headers: { 
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data && response.data.url) {
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, response.data.url]
          }));

          const reader = new FileReader();
          reader.onloadend = () => {
            setImagePreviews(prev => [...prev, reader.result]);
          };
          reader.readAsDataURL(file);
        }
      } catch (error) {
        console.error('Image upload error:', error);
        setError(`Failed to upload image: ${error.response?.data?.detail || error.message}`);
      }
    }

    setUploadingImages(false);
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate scheduling rules for Time Bound jobs
    if (!isOpenTime) {
      if (formData.preferred_date && formData.bidding_deadline_date &&
          formData.preferred_date < formData.bidding_deadline_date) {
        setError('Preferred date cannot be before the bidding close date.');
        return;
      }
      if (isSameDay && formData.preferred_start_time) {
        const sb = addHoursToTime(effectiveBiddingTime, 2);
        if (!sb || sb.overflow || formData.preferred_start_time < sb.time) {
          setError('For a same-day job, the start time must be at least 2 hours after bidding closes.');
          return;
        }
      }
      if (formData.preferred_start_time && formData.preferred_end_time) {
        const eb = addHoursToTime(formData.preferred_start_time, 2);
        if (!eb || eb.overflow || formData.preferred_end_time < eb.time) {
          setError('End time must be at least 2 hours after the start time.');
          return;
        }
      }
      if (formData.deadline && formData.preferred_date &&
          formData.deadline < formData.preferred_date) {
        setError('Job completion deadline cannot be earlier than the preferred date.');
        return;
      }
    }

    setLoading(true);

    try {
      // Combine date and time for bidding_deadline
      let bidding_deadline = null;
      if (formData.bidding_deadline_date) {
        const dateStr = formData.bidding_deadline_date;
        const timeStr = formData.bidding_deadline_time || '23:59'; // Default to end of day
        bidding_deadline = `${dateStr}T${timeStr}:00`;
      }

      const submitData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        budget: parseFloat(formData.budget),
        currency: formData.currency,
        job_type: formData.job_type,
        location: formData.location,
        location_coordinates: formData.location_coordinates,
        // Open Time jobs are scheduled later via the post-bid agreement
        preferred_date: isOpenTime ? '' : formData.preferred_date,
        preferred_start_time: isOpenTime ? '' : formData.preferred_start_time,
        preferred_end_time: isOpenTime ? '' : formData.preferred_end_time,
        deadline: isOpenTime ? '' : formData.deadline,
        bidding_deadline: bidding_deadline,
        images: formData.images
      };

      const response = await axios.post(`${API}/jobs`, submitData);
      const jobOrderCode = response.data.job_order_code;
      
      if (jobOrderCode) {
        setCreatedJobCode(jobOrderCode);
        setShowSuccessModal(true);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  // Get today's date for min date validation
  const today = new Date().toISOString().split('T')[0];

  // Add N hours to an HH:MM time; returns { time, overflow } where overflow=true
  // means the result crosses midnight (not valid within the same day).
  const addHoursToTime = (hhmm, hours) => {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(Number);
    const total = h * 60 + m + hours * 60;
    if (total >= 24 * 60) return { time: null, overflow: true };
    const nh = Math.floor(total / 60), nm = total % 60;
    return { time: `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`, overflow: false };
  };

  const isOpenTime = formData.job_type === 'open_time';
  // Preferred date can be the bidding-close date (same day) or later
  const minPreferredDate = formData.bidding_deadline_date || today;
  // Completion deadline can be the preferred date or later
  const minDeadline = formData.preferred_date || minPreferredDate;

  // Bidding time defaults to end of day when left blank
  const effectiveBiddingTime = formData.bidding_deadline_time || '23:59';
  // Same-day job: preferred date equals the bidding-close date
  const isSameDay = !isOpenTime && !!formData.preferred_date && !!formData.bidding_deadline_date
    && formData.preferred_date === formData.bidding_deadline_date;
  // Start must be >= bidding close + 2h on the same day
  const startBound = isSameDay ? addHoursToTime(effectiveBiddingTime, 2) : null;
  const minStartTime = startBound && !startBound.overflow ? startBound.time : undefined;
  const sameDayStartImpossible = isSameDay && (!startBound || startBound.overflow);
  // End must be >= start + 2h
  const endBound = formData.preferred_start_time ? addHoursToTime(formData.preferred_start_time, 2) : null;
  const minEndTime = endBound && !endBound.overflow ? endBound.time : undefined;
  const endTimeImpossible = !!formData.preferred_start_time && (!endBound || endBound.overflow);

  const getCurrencySymbol = (currency) => {
    const symbols = { PHP: '₱', USD: '$', EUR: '€', GBP: '£' };
    return symbols[currency] || currency;
  };

  // Block users with rejected ID verification
  if (user?.id_verification_status === 'rejected') {
    const reasons = user?.kyc_rejection_reasons || [];
    const latestReason = reasons.length > 0 ? reasons[reasons.length - 1]?.reason : 'Your submitted documents did not meet our requirements.';
    return (
      <div className="min-h-screen bg-gray-50" data-testid="create-job-rejected">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-lg mx-auto px-4 py-20">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">ID Verification Rejected</h2>
            <p className="text-gray-600 text-sm mb-4">
              Your identity verification has been rejected. You cannot post jobs until your ID is verified.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6 text-left">
              <p className="text-sm font-semibold text-red-700 mb-1">Rejection Reason:</p>
              <p className="text-sm text-red-600">{latestReason}</p>
            </div>
            <div className="flex gap-3 justify-center">
              <button onClick={() => navigate('/account-settings')} className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg" data-testid="go-to-settings-btn">Go to Account Settings</button>
              <button onClick={() => navigate('/dashboard')} className="border border-gray-300 text-gray-700 px-6 py-2.5 rounded-lg font-semibold hover:bg-gray-50" data-testid="go-to-dashboard-btn">Dashboard</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Block users who are not yet KYC verified
  if (!user?.id_verified) {
    const status = user?.id_verification_status;
    const isPending = status === 'pending' || status === 'pending_admin_review';
    return (
      <div className="min-h-screen bg-gray-50" data-testid="create-job-not-verified">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-lg mx-auto px-4 py-20">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {isPending ? 'ID Verification Pending' : 'ID Verification Required'}
            </h2>
            <p className="text-gray-600 text-sm mb-6">
              {isPending
                ? 'Your ID documents are currently being reviewed. You will be able to post jobs once your identity is verified.'
                : 'You need to complete ID verification before you can post jobs. Please submit your documents from your Account Settings.'}
            </p>
            <div className="flex gap-3 justify-center">
              {!isPending && (
                <button onClick={() => navigate('/account-settings')} className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg" data-testid="go-to-settings-btn">Submit ID Documents</button>
              )}
              <button onClick={() => navigate('/dashboard')} className="border border-gray-300 text-gray-700 px-6 py-2.5 rounded-lg font-semibold hover:bg-gray-50" data-testid="go-to-dashboard-btn">Back to Dashboard</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="create-job-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-blue-600 transition"
            data-testid="back-button"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent" data-testid="page-title">
            {t('createJob.title')}
          </h1>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded" data-testid="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="create-job-form">
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title">
                {t('createJob.jobTitle')} *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('createJob.jobTitlePlaceholder')}
                required
                data-testid="title-input"
              />
            </div>

            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="category">
                {t('createJob.category')} *
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                data-testid="category-select"
              >
                <option value="">{t('createJob.selectCategory')}</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
                {t('createJob.description')} *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="6"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                placeholder={t('createJob.descPlaceholder')}
                data-testid="description-input"
              ></textarea>
            </div>

            {/* Budget */}
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="budget">
                {t('createJob.budget')} *
              </label>
              <div className="flex max-w-md">
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="px-3 py-2 border border-r-0 border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                >
                  <option value="PHP">₱ PHP</option>
                  <option value="USD">$ USD</option>
                  <option value="EUR">€ EUR</option>
                  <option value="GBP">£ GBP</option>
                </select>
                <input
                  type="number"
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="0.00"
                  data-testid="budget-input"
                />
              </div>
            </div>

            {/* Scheduling Type Toggle */}
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('createJob.schedulingType')} *
              </label>
              <div className="inline-flex p-1 bg-gray-100 rounded-xl" data-testid="job-type-toggle">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, job_type: 'time_bound' }))}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${
                    !isOpenTime ? 'bg-white text-orange-600 shadow' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="job-type-time-bound"
                >
                  {t('createJob.timeBound')}
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, job_type: 'open_time' }))}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${
                    isOpenTime ? 'bg-white text-orange-600 shadow' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="job-type-open-time"
                >
                  {t('createJob.openTime')}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {isOpenTime
                  ? t('createJob.openTimeHint')
                  : t('createJob.timeBoundHint')}
              </p>
            </div>

            {/* Bidding Closes On (always shown) */}
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="bidding_deadline_date">
                {t('createJob.biddingClosesOn')} *
              </label>
              <div className="grid grid-cols-2 gap-3 max-w-md">
                <div>
                  <input
                    type="date"
                    id="bidding_deadline_date"
                    name="bidding_deadline_date"
                    value={formData.bidding_deadline_date}
                    onChange={handleChange}
                    min={today}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="bidding-deadline-date-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('createJob.date')}</p>
                </div>
                <div>
                  <input
                    type="time"
                    id="bidding_deadline_time"
                    name="bidding_deadline_time"
                    value={formData.bidding_deadline_time}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="bidding-deadline-time-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('createJob.timeDefault')}</p>
                </div>
              </div>
              <p className="text-xs text-orange-600 mt-2">
                {t('createJob.biddingWarn')}
              </p>
            </div>

            {/* Time Bound scheduling fields */}
            {!isOpenTime ? (
              <div className="mb-6 space-y-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="preferred_date">
                    {t('createJob.preferredDate')}
                  </label>
                  <input
                    type="date"
                    id="preferred_date"
                    name="preferred_date"
                    value={formData.preferred_date}
                    onChange={handleChange}
                    min={minPreferredDate}
                    disabled={!formData.bidding_deadline_date}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    data-testid="preferred-date-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.bidding_deadline_date
                      ? t('createJob.preferredDateHint')
                      : t('createJob.preferredDateHintDisabled')}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="preferred_start_time">
                      {t('createJob.startTime')}
                    </label>
                    <input
                      type="time"
                      id="preferred_start_time"
                      name="preferred_start_time"
                      value={formData.preferred_start_time}
                      onChange={handleChange}
                      min={minStartTime}
                      disabled={sameDayStartImpossible}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      data-testid="preferred-start-time-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {sameDayStartImpossible
                        ? t('createJob.startImpossible')
                        : isSameDay
                          ? t('createJob.startSameDay')
                          : t('createJob.startNormal')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="preferred_end_time">
                      {t('createJob.endTime')}
                    </label>
                    <input
                      type="time"
                      id="preferred_end_time"
                      name="preferred_end_time"
                      value={formData.preferred_end_time}
                      onChange={handleChange}
                      min={minEndTime}
                      disabled={!formData.preferred_start_time || endTimeImpossible}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      data-testid="preferred-end-time-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {!formData.preferred_start_time
                        ? t('createJob.endSetStart')
                        : endTimeImpossible
                          ? t('createJob.endImpossible')
                          : t('createJob.endNormal')}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="deadline">
                    {t('createJob.completionDeadline')}
                  </label>
                  <input
                    type="date"
                    id="deadline"
                    name="deadline"
                    value={formData.deadline}
                    onChange={handleChange}
                    min={minDeadline}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="deadline-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('createJob.completionHint')}</p>
                </div>
              </div>
            ) : (
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="open-time-info">
                <p className="text-sm font-semibold text-blue-800 mb-1">{t('createJob.openTimeTitle')}</p>
                <p className="text-sm text-blue-700">
                  {t('createJob.openTimeDesc')}
                </p>
              </div>
            )}

            {/* Location Selection */}
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('createJob.jobLocation')}
              </label>
              
              {/* Option to use registered address - only show if user has one */}
              {getUserRegisteredAddress() && (
                <div className="mb-3 space-y-2">
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="locationOption"
                      checked={useRegisteredAddress && !useCustomLocation}
                      onChange={() => {
                        setUseRegisteredAddress(true);
                        setUseCustomLocation(false);
                        setFormData({ ...formData, location: getUserRegisteredAddress(), location_coordinates: user?.location_coordinates || null });
                      }}
                      className="w-4 h-4 text-orange-500"
                    />
                    <div>
                      <p className="font-medium text-gray-800">{t('createJob.useRegistered')}</p>
                      <p className="text-sm text-gray-500">{getUserRegisteredAddress()}</p>
                    </div>
                  </label>
                  
                  {savedLocations.length > 0 && (
                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="locationOption"
                        checked={!useRegisteredAddress && !useCustomLocation}
                        onChange={() => {
                          setUseRegisteredAddress(false);
                          setUseCustomLocation(false);
                        }}
                        className="w-4 h-4 text-orange-500"
                      />
                      <div>
                        <p className="font-medium text-gray-800">{t('createJob.useSaved')}</p>
                        <p className="text-sm text-gray-500">{t('createJob.chooseSaved')}</p>
                      </div>
                    </label>
                  )}
                  
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="locationOption"
                      checked={useCustomLocation}
                      onChange={() => {
                        setUseRegisteredAddress(false);
                        setUseCustomLocation(true);
                        setFormData({ ...formData, location: '', location_coordinates: null });
                      }}
                      className="w-4 h-4 text-orange-500"
                    />
                    <div>
                      <p className="font-medium text-gray-800">{t('createJob.useDifferent')}</p>
                      <p className="text-sm text-gray-500">{t('createJob.searchOrMap')}</p>
                    </div>
                  </label>
                </div>
              )}

              {/* When no registered address but has saved locations - show as primary option */}
              {!getUserRegisteredAddress() && savedLocations.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm text-gray-600 mb-2">{t('createJob.selectSaved')}</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {savedLocations.map((loc) => {
                      const locAddress = loc.full_address || [loc.street, loc.barangay, loc.city, loc.province].filter(Boolean).join(', ');
                      return (
                        <button
                          key={loc.id}
                          type="button"
                          onClick={() => handleSavedLocationSelect(loc.id)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                            formData.location === locAddress
                              ? 'bg-orange-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          data-testid={`saved-location-${loc.id}`}
                        >
                          📍 {loc.label}
                          {loc.is_default && <span className="ml-1 text-xs">(Default)</span>}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => {
                        setUseCustomLocation(true);
                        setFormData({ ...formData, location: '', location_coordinates: null });
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                        useCustomLocation
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      data-testid="custom-location-btn"
                    >
                      🗺️ {t('createJob.differentLocation')}
                    </button>
                  </div>
                </div>
              )}

              {/* Saved locations selector when user has registered address */}
              {getUserRegisteredAddress() && !useRegisteredAddress && !useCustomLocation && savedLocations.length > 0 && (
                <div className="mb-3">
                  <div className="flex flex-wrap gap-2">
                    {savedLocations.map((loc) => {
                      const locAddress = loc.full_address || [loc.street, loc.barangay, loc.city, loc.province].filter(Boolean).join(', ');
                      return (
                        <button
                          key={loc.id}
                          type="button"
                          onClick={() => handleSavedLocationSelect(loc.id)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                            formData.location === locAddress
                              ? 'bg-orange-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          data-testid={`saved-location-${loc.id}`}
                        >
                          📍 {loc.label}
                          {loc.is_default && <span className="ml-1 text-xs">(Default)</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Custom location picker */}
              {(useCustomLocation || (!getUserRegisteredAddress() && savedLocations.length === 0)) && (
                <div className="space-y-3">
                  <PHAddressSelect
                    province={customAddr.province}
                    city={customAddr.city}
                    barangay={customAddr.barangay}
                    onChange={(next) => {
                      setCustomAddr(next);
                      const composed = [next.barangay, next.city, next.province].filter(Boolean).join(', ');
                      setFormData((prev) => ({ ...prev, location: composed, location_coordinates: null }));
                    }}
                    idPrefix="job-location"
                  />
                  <div className="text-xs text-gray-400 text-center">{t('createJob.orPinpoint')}</div>
                  <LocationPicker
                    onLocationSelect={handleLocationSelect}
                    placeholder={t('createJob.mapPlaceholder')}
                  />
                </div>
              )}

              {formData.location && (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-sm text-green-700 font-medium">{formData.location}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Job Images Section */}
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('createJob.referenceImages')}
              </label>
              <p className="text-sm text-gray-500 mb-3">
                {t('createJob.imagesHint')}
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
                {imagePreviews.map((preview, index) => (
                  <div key={preview} className="relative group" data-testid={`job-image-${index}`}>
                    <img
                      src={preview}
                      alt={`Job reference ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition hover:bg-red-600"
                      data-testid={`remove-job-image-${index}`}
                    >
                      ×
                    </button>
                  </div>
                ))}

                {formData.images.length < 5 && (
                  <div className="flex flex-col gap-2">
                    <label className="border-2 border-dashed border-gray-300 rounded-lg h-24 flex flex-col items-center justify-center cursor-pointer hover:border-orange-500 transition" data-testid="add-job-image-btn">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImages}
                      />
                      {uploadingImages ? (
                        <span className="text-gray-500 text-sm">{t('createJob.uploading')}</span>
                      ) : (
                        <>
                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <span className="text-xs text-gray-500 mt-1">{t('createJob.addPhoto')}</span>
                        </>
                      )}
                    </label>
                    <label className="border-2 border-dashed border-blue-300 rounded-lg h-24 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition bg-blue-50/50" data-testid="capture-job-image-btn">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImages}
                      />
                      {uploadingImages ? (
                        <span className="text-blue-500 text-sm">{t('createJob.capturing')}</span>
                      ) : (
                        <>
                          <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-xs text-blue-500 mt-1">{t('createJob.useCamera')}</span>
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>

              {formData.images.length === 0 && (
                <p className="text-xs text-gray-400 italic">{t('createJob.noImages')}</p>
              )}
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 font-semibold"
                data-testid="cancel-button"
              >
                {t('createJob.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading || uploadingImages}
                className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg hover:shadow-lg font-semibold disabled:bg-gray-400 transition"
                data-testid="submit-button"
              >
                {loading ? t('createJob.posting') : t('createJob.postJob')}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 text-center">
            <div className="mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('createJob.successTitle')}</h3>
              <p className="text-gray-600 mb-4">{t('createJob.successDesc')}</p>
            </div>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-2">{t('createJob.yourCode')}</p>
              <p className="text-2xl font-mono font-bold text-orange-600" data-testid="job-order-code">
                #JO-{createdJobCode}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                {t('createJob.trackHint')}
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(`JO-${createdJobCode}`);
                }}
                className="flex-1 py-2 border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50 font-semibold"
              >
                {t('createJob.copyCode')}
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-1 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-semibold"
                data-testid="go-to-dashboard-btn"
              >
                {t('createJob.goToDashboard')}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CreateJob;
